import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import styles from "./NotificationDashboard.module.css";
import { UsersService } from "../../globals/services/users.service";
import { MsgService } from "../../globals/services/msgs.service";
import EmailComposer from "./EmailComposer/EmailComposer";
import ConfirmationModal from "../../globals/core/ConfirmationModal/ConfirmationModal";
import { Snackbar, IconButton, Button } from "@material-ui/core";
import UserList from "./UserList/UserList";
import ReminderList from "./ReminderList/ReminderList";

const NotificationDashboard = (props) => {
  const { item } = props.location.state;
  let history = useHistory();
  const initialState = {
    id: item?._id,
    entityTitle: item?.eventTitle || "No title",
    team: item?.sessionTeam || [],
    entityParticipants: [],
    participants: item?.participants || item.curriculaUsers || [],
    backParticipants: [],
    seminarTasks: item?.seminarTasks || [],
    module: item?.participants ? "seminar" : "curricula",
  };

  const initialMessageState = {
    msgType: "Email",
    msgLanguage: "English",
    msgTitle: "",
    msgBody: "",
  };

  const usersService = UsersService();
  const msgService = MsgService();
  const [data, setData] = useState(initialState);
  const { me } = useSelector((state) => state.auth);
  const [message, setMessage] = useState(initialMessageState);
  const [confirmationModal, setConfirmationModal] = useState({ open: false, title: "", message: "", buttons: [] });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", action: null });

  useEffect(() => {
    if (data.backParticipants.length === 0) {
      const ids = data.participants.map((participant) => participant._id);

      usersService.getUserByArrayOfIds(ids).then((res) => {
        setData({
          ...data,
          entityParticipants: res,
          backParticipants: res,
        });
      });
    }
  }, [data?.participants]);

  const updateMessage = (update) => {
    setMessage((prevMsg) => ({
      ...prevMsg,
      ...update,
    }));
  };

  const clearMsgForm = async () => {
    setMessage((prevMsg) => ({
      ...prevMsg,
      msgBody: "",
    }));
  };

  const removeParticipant = (participant) => {
    const newParticipants = data.entityParticipants.filter((item) => item._id !== participant._id);
    setData({
      ...data,
      entityParticipants: newParticipants,
    });
  };

  const filterParticipant = (attr) => {
    let newParticipants = [];
    if (attr === "terms") {
      newParticipants = data.entityParticipants.filter((item) => !item.termsApproveDate);
    } else if (attr === "onlineStatus") {
      newParticipants = data.entityParticipants.filter((item) => item.socketSession);
    } else if (attr === "device") {
      newParticipants = data.entityParticipants.filter((item) => item.mobileDeviceId);
    }

    setData({
      ...data,
      entityParticipants: newParticipants,
    });
  };

  const refreshParticipantList = () => {
    setData({
      ...data,
      entityParticipants: data.backParticipants,
    });
  };

  const sendTestMsgAfterConfirm = async () => {
    let snackBarMessage = "Error to send Notification, Try again";
    try {
      setConfirmationModal((prev) => {
        return {
          ...prev,
          buttons: prev.buttons.map((button) => (button.label === "Send Test" ? { ...button, loading: true } : button)),
        };
      });
      const mailerRes = await usersService.sendEmailNotification([me._id], message.msgBody, message.msgTitle, message.msgLanguage);
      if (mailerRes?.length > 0) {
        snackBarMessage = "Notification was sent to admins as TEST ONLY";
      }
    } catch (error) {
      console.log(error);
    }

    setConfirmationModal({ open: false });
    setSnackbar({ open: true, message: snackBarMessage, action: clearActionSnackBar });
  };

  const sendMsgAfterConfirm = async () => {
    setConfirmationModal((prev) => {
      return {
        ...prev,
        buttons: prev.buttons.map((button) => (button.label === "Confirm" ? { ...button, loading: true } : button)),
      };
    });
    let snackBarMessage = "Error to send Notification, Try again";
    try {
      if (message.msgType === "Internal") {
        await msgService.sendInternalNotification(message.msgBody, data.participants);
        snackBarMessage = "Notification was sent successfully to event participants";
      } else if (message.msgType === "Mobile") {
        const success = await sendMobileNotification();
        snackBarMessage = success ? "Notification was sent successfully to event participants" : "Error to send Notification, Try again";
      } else {
        const success = await sendEmail();
        snackBarMessage = success ? "Notification was sent successfully to event participants" : "Error to send Notification, Try again";
      }
    } catch (e) {
      console.log(e);
    }
    setConfirmationModal({ open: false });
    setSnackbar({ open: true, message: snackBarMessage, action: null });
  };

  const sendEmail = async () => {
    const ids = data.entityParticipants.map((participant) => participant._id).filter((id) => id != null) || [];
    const mailerRes = await usersService.sendEmailNotification(ids, message.msgBody, message.msgTitle, message.msgLanguage);
    return mailerRes?.length > 0;
  };

  const sendMobileNotification = async () => {
    const msgObj = {
      title: message.msgTitle,
      body: message.msgBody,
    };
    const mobileTokens = data.entityParticipants.map((participant) => participant.mobile).filter((mobile) => mobile != null);
    const notifyServiceRes = await usersService.sendMobileNotification(mobileTokens, msgObj);
    return notifyServiceRes.length > 0;
  };

  const goBack = () => {
    history.goBack();
  };

  const onSendMessage = () => {
    setConfirmationModal({
      open: true,
      title: "Send Message",
      buttons: [
        { label: "Send Test", style: "info", onClick: sendTestMsgAfterConfirm },
        { label: "Confirm", style: "error", onClick: sendMsgAfterConfirm },
      ],
    });
  };

  const handleCloseSnackBar = () => {
    setSnackbar({ open: false });
  };

  const clearActionSnackBar = (
    <>
      <Button color="primary" size="small" onClick={clearMsgForm}>
        Clear
      </Button>
      <IconButton size="small" aria-label="close" color="inherit" onClick={handleCloseSnackBar}>
        X
      </IconButton>
    </>
  );

  return (
    <>
      <div className={styles.notificationContainer}>
        <button className={styles.closeBtn} onClick={goBack}>
          X
        </button>
        <div className="col-7">
          <EmailComposer message={message} updateMessage={updateMessage} onSend={onSendMessage} />
        </div>
        <div className={`col-5`}>
          <div className={styles.rightColumn}>
            <UserList
              participants={data?.entityParticipants}
              onRemoveParticipant={removeParticipant}
              onFilterParticipants={filterParticipant}
              onResetParticipants={refreshParticipantList}
            />
            {data?.seminarTasks && <ReminderList tasks={data.seminarTasks} eventId={data.id} eventTitle={data.entityTitle} />}
          </div>
        </div>
      </div>
      <ConfirmationModal {...confirmationModal} onClose={() => setConfirmationModal({ open: false })} />
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        open={snackbar.open}
        autoHideDuration={60000}
        onClose={() => setSnackbar({ open: false })}
        message={snackbar.message}
        action={snackbar.action}
      />
    </>
  );
};


export default NotificationDashboard;
